import axios, { AxiosError } from "axios";
import {
  useQuery,
  useQueryClient,
  useMutation,
  UseQueryResult,
  UseMutationResult,
} from "react-query";
import { ShiftSite, ShiftSiteGroup } from "../model/ShiftSite";
import { HttpClientError } from "../../../../providers/HttpClient";

// ── ShiftSiteGroup CRUD ─────────────────────────────────────────

interface ShiftSiteGroupListResponse {
  results: ShiftSiteGroup[];
}

const listShiftSiteGroups = async (): Promise<ShiftSiteGroupListResponse> => {
  const { data } = await axios.get("/shift-site-groups/");
  return data;
};

const useListShiftSiteGroups = (): UseQueryResult<
  ShiftSiteGroupListResponse,
  Error | AxiosError
> =>
  useQuery<ShiftSiteGroupListResponse, Error | AxiosError>(
    ["ShiftSiteGroupsList"],
    listShiftSiteGroups
  );

const useCreateShiftSiteGroup = (): UseMutationResult<
  ShiftSiteGroup,
  Error | AxiosError,
  { name: string }
> =>
  useMutation(async (payload) => {
    try {
      const { data } = await axios.post("/shift-site-groups/", payload);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      throw new Error(HttpClientError(axiosError));
    }
  });

// ── ShiftSite CRUD ──────────────────────────────────────────────

interface ShiftSiteListResponse {
  results: ShiftSite[];
}

const listShiftSites = async (): Promise<ShiftSiteListResponse> => {
  const { data } = await axios.get("/shift-sites/");
  return data;
};

const useListShiftSites = (): UseQueryResult<
  ShiftSiteListResponse,
  Error | AxiosError
> =>
  useQuery<ShiftSiteListResponse, Error | AxiosError>(
    ["ShiftSitesList"],
    listShiftSites
  );

const getShiftSite = async (id: string): Promise<ShiftSite> => {
  const { data } = await axios.get(`/shift-sites/${id}/`);
  return data;
};

const useGetShiftSite = (
  id: string,
  enabled = true
): UseQueryResult<ShiftSite, Error | AxiosError> =>
  useQuery<ShiftSite, Error | AxiosError>(
    ["ShiftSite", id],
    () => getShiftSite(id),
    { enabled: enabled && Boolean(id) }
  );

interface CreateShiftSitePayload {
  name: string;
  site_group?: string;
  client?: string;
}

const useCreateShiftSite = (): UseMutationResult<
  ShiftSite,
  Error | AxiosError,
  CreateShiftSitePayload
> =>
  useMutation(async (payload) => {
    try {
      const { data } = await axios.post("/shift-sites/", payload);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      throw new Error(HttpClientError(axiosError));
    }
  });

interface UpdateShiftSitePayload {
  id: string;
  name?: string;
  site_group?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  radius_allowance?: number | null;
  late_arrival_threshold_minutes?: number | null;
}

const useUpdateShiftSite = (): UseMutationResult<
  ShiftSite,
  Error | AxiosError,
  UpdateShiftSitePayload
> => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ id, ...payload }) => {
      try {
        const { data } = await axios.patch(`/shift-sites/${id}/`, payload);
        return data;
      } catch (error) {
        const axiosError = error as AxiosError;
        throw new Error(HttpClientError(axiosError));
      }
    },
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(["ShiftSite", variables.id]);
        queryClient.invalidateQueries(["ShiftSitesList"]);
        queryClient.invalidateQueries(["ShiftSitesOverviewApiService"]);
      },
    }
  );
};

// ── ShiftSiteGroup update ───────────────────────────────────────

interface UpdateShiftSiteGroupPayload {
  id: string;
  name: string;
}

const useUpdateShiftSiteGroup = (): UseMutationResult<
  ShiftSiteGroup,
  Error | AxiosError,
  UpdateShiftSiteGroupPayload
> => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ id, ...payload }) => {
      try {
        const { data } = await axios.patch(
          `/shift-site-groups/${id}/`,
          payload
        );
        return data;
      } catch (error) {
        const axiosError = error as AxiosError;
        throw new Error(HttpClientError(axiosError));
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["ShiftSiteGroupsList"]);
        queryClient.invalidateQueries(["ShiftSitesList"]);
        queryClient.invalidateQueries(["ShiftSite"]);
        queryClient.invalidateQueries(["ShiftSitesOverviewApiService"]);
      },
    }
  );
};

// ── ShiftLocation shift-site assignment ─────────────────────────

interface AssignLocationToShiftSitePayload {
  locationId: string;
  site: string | null;
}

const useAssignLocationToShiftSite = (): UseMutationResult<
  unknown,
  Error | AxiosError,
  AssignLocationToShiftSitePayload
> =>
  useMutation(async ({ locationId, site }) => {
    try {
      const { data } = await axios.patch(`/shift-locations/${locationId}/`, {
        site,
      });
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      throw new Error(HttpClientError(axiosError));
    }
  });

export {
  useListShiftSiteGroups,
  useCreateShiftSiteGroup,
  useUpdateShiftSiteGroup,
  useListShiftSites,
  useGetShiftSite,
  useCreateShiftSite,
  useUpdateShiftSite,
  useAssignLocationToShiftSite,
};
